import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Calendar, Clock, Share2, Twitter, Facebook, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';
import Footer from '../components/Footer';
import blogImages from '../data/blogImages';

const API = process.env.REACT_APP_BACKEND_URL;
// County mugshot URLs are http:// and only load via our proxy (mixed-content + hotlink).
// Other absolute image URLs (e.g. hosted hero art) are https and load directly.
const proxied = (u) => {
  if (!u) return u;
  if (/mecklenburgcountync\.gov/i.test(u)) return `${API}/api/mugshots/image-proxy?url=${encodeURIComponent(u)}`;
  return u;
};

// Blog post content database
const blogPosts = {
  'welcome-to-jailbirds': {
    title: "Welcome to JailBirds: A New Way to Test Your Instincts",
    date: "December 15, 2025",
    category: "Announcement",
    readTime: "4 min read",
    content: `
      <p class="lead">We're thrilled to announce the official launch of JailBirds, a unique guessing game that challenges your ability to match mugshots with criminal charges using real public records from Mecklenburg County, North Carolina.</p>

      <h2>What Makes JailBirds Different?</h2>
      <p>Unlike other trivia games, JailBirds uses real, publicly available arrest records. Every mugshot you see is from an actual arrest in Mecklenburg County, and every charge is genuine. This creates a fascinating and educational experience that goes beyond simple entertainment.</p>
      
      <p>Our game taps into something fundamentally human – our instinct to make quick judgments based on visual information. Can you really tell what someone was arrested for just by looking at them? JailBirds puts that question to the test.</p>

      <h2>How It Works</h2>
      <p>Each game consists of 11 regular questions plus a special JailBird Bonus round. You'll see a mugshot and four possible charges – your job is to pick the correct one. It sounds simple, but you'll quickly discover that appearances can be deceiving!</p>

      <h3>The Scoring System</h3>
      <ul>
        <li><strong>Regular Questions:</strong> 100 points each for correct answers</li>
        <li><strong>Bonus Round:</strong> Time-based scoring with multipliers</li>
        <li><strong>Perfect Game:</strong> Over 1,100 points possible!</li>
      </ul>

      <h2>Fresh Content Daily</h2>
      <p>One of our key features is daily updated content. Our automated system pulls fresh arrest records from the Mecklenburg County Sheriff's Office every day, ensuring you never see the same game twice. This keeps the experience fresh and engaging for returning players.</p>

      <h2>Compete on the Leaderboard</h2>
      <p>Create a nickname and compete against players from around the world. Our leaderboards track daily, weekly, monthly, and all-time high scores. Can you reach the top?</p>

      <h2>A Note on Ethics</h2>
      <p>We take the ethical implications of our game seriously. All information we display is publicly available, and we always remind players that an arrest does not equal guilt. Everyone shown in our game is presumed innocent until proven otherwise in a court of law.</p>

      <p>We've designed JailBirds to be entertaining while also providing a window into the criminal justice system. We hope it sparks curiosity and perhaps even empathy.</p>

      <h2>Get Started Today</h2>
      <p>Ready to test your detective instincts? Head to our homepage and start playing – it's completely free! Create your nickname, pick an avatar, and see how you stack up against other players.</p>

      <p>Thank you for being part of the JailBirds community. We're excited to have you here!</p>
    `
  },
  'daily-updates-explained': {
    title: "How We Keep the Game Fresh with Daily Updates",
    date: "December 16, 2025",
    category: "Behind the Scenes",
    readTime: "5 min read",
    content: `
      <p class="lead">Ever wonder how JailBirds manages to stay interesting day after day? The secret is our automated content pipeline that pulls fresh arrest records daily. Here's a behind-the-scenes look at how it all works.</p>

      <h2>The Data Source</h2>
      <p>All our mugshots and arrest information come from the Mecklenburg County Sheriff's Office in North Carolina. This is public information that anyone can access – we simply make it more engaging through our game format.</p>

      <p>The Sheriff's Office maintains an online database of recent arrests, including booking photos (mugshots), charges, arrest dates, and other relevant information. This data is updated multiple times per day as new arrests are processed.</p>

      <h2>Our Automated Pipeline</h2>
      <p>Every day, our system automatically:</p>
      <ol>
        <li><strong>Fetches New Records:</strong> We pull the latest arrest data from the public database</li>
        <li><strong>Validates Data:</strong> Not all records are suitable for the game – we need clear mugshots and real charges</li>
        <li><strong>Filters Content:</strong> We remove records with missing images or placeholder charges</li>
        <li><strong>Stores for Gameplay:</strong> Valid records are saved to our database for use in games</li>
      </ol>

      <h2>Quality Control</h2>
      <p>Not every arrest record makes it into the game. Our validation process ensures:</p>
      <ul>
        <li>The mugshot image is available and clear</li>
        <li>The charges are real (not "pending" or "not available")</li>
        <li>The record has all required information</li>
      </ul>

      <p>On a typical day, we might fetch 50-70 new records, of which 40-55 pass our quality checks. This ensures every game uses high-quality, complete data.</p>

      <h2>Smart Batch Selection</h2>
      <p>To provide the best gaming experience, our system intelligently selects which data to use. If today's scrape only yields 20 valid records, but yesterday's has 50, we'll use yesterday's data to ensure a full 12-question game.</p>

      <h2>Why Daily Updates Matter</h2>
      <p>Fresh content is crucial for player engagement. Nobody wants to see the same mugshots repeatedly. Our daily updates mean:</p>
      <ul>
        <li>New challenges every day</li>
        <li>Returning players see fresh faces</li>
        <li>The game reflects current real-world data</li>
        <li>Leaderboard competition stays fair and interesting</li>
      </ul>

      <h2>Technical Challenges</h2>
      <p>Running an automated scraper isn't without challenges. We've had to solve issues like:</p>
      <ul>
        <li>Website structure changes breaking our parser</li>
        <li>Rate limiting and server timeouts</li>
        <li>Handling missing or malformed data</li>
        <li>Ensuring consistent data quality</li>
      </ul>

      <p>Our system is designed to be resilient, with fallbacks and error handling to ensure the game always has content to serve.</p>

      <h2>Looking Ahead</h2>
      <p>We're constantly improving our pipeline. Future enhancements might include:</p>
      <ul>
        <li>Support for additional counties and states</li>
        <li>More sophisticated content filtering</li>
        <li>Historical data for "throwback" game modes</li>
      </ul>

      <p>Stay tuned for more behind-the-scenes looks at how JailBirds works!</p>
    `
  },
  'top-player-strategies': {
    title: "Top Strategies from Our Highest Scorers",
    date: "December 17, 2025",
    category: "Tips & Tricks",
    readTime: "6 min read",
    content: `
      <p class="lead">We reached out to some of our top-performing players to learn their secrets. Here's what separates the casual players from the leaderboard champions.</p>

      <h2>The Psychology of Guessing</h2>
      <p>Our top players approach each mugshot like a puzzle. They've learned that certain visual cues can sometimes correlate with specific types of charges – though they're quick to point out that stereotypes are often wrong.</p>

      <h3>Strategy #1: Look Beyond the Obvious</h3>
      <p>"The biggest mistake new players make is going with their gut reaction based on stereotypes," says one top scorer. "I've learned that the most obvious-looking answer is often wrong. The game seems to specifically include cases that defy expectations."</p>

      <h3>Strategy #2: Read All Options First</h3>
      <p>Several players emphasized the importance of reading all four charge options before making a decision. "Sometimes the answer becomes clear when you see all the choices together. One option might just 'feel right' in context."</p>

      <h3>Strategy #3: Consider the Demographics</h3>
      <p>Different types of crimes have different demographic patterns statistically. While this isn't foolproof, understanding these patterns can sometimes help narrow down options.</p>

      <h2>Mastering the Bonus Round</h2>
      <p>The JailBird Bonus round is where games are won or lost. Here's how top players approach it:</p>

      <h3>Stay Calm Under Pressure</h3>
      <p>"The timer creates anxiety, which leads to mistakes," one player explains. "I take a deep breath before the bonus round and remind myself that a thoughtful answer in 6 seconds is better than a panicked wrong answer in 2 seconds."</p>

      <h3>Quick Elimination</h3>
      <p>Instead of trying to identify the right answer immediately, experienced players quickly eliminate obviously wrong choices. "If I can get it down to two options in the first 2 seconds, I have plenty of time to make the final call."</p>

      <h3>Trust Your First Instinct</h3>
      <p>Interestingly, several top players mentioned that overthinking in the bonus round hurts their scores. "Your subconscious picks up on patterns you're not even aware of. In the bonus round, I usually go with my first instinct."</p>

      <h2>Building Consistency</h2>
      <p>High scores aren't just about one lucky game – they're about consistent performance across multiple games.</p>

      <h3>Play Regularly</h3>
      <p>"I play 3-4 games every day," says one weekly leaderboard champion. "It keeps my pattern recognition sharp and helps me stay familiar with the types of charges that commonly appear."</p>

      <h3>Learn from Mistakes</h3>
      <p>After each question, pay attention to the correct answer. Over time, you'll start noticing patterns that improve your accuracy.</p>

      <h3>Don't Chase Perfect Games</h3>
      <p>"Trying too hard for a perfect game actually hurts your score," one player notes. "The pressure makes you second-guess yourself. Just focus on each question individually."</p>

      <h2>Common Pitfalls to Avoid</h2>
      <ul>
        <li><strong>Stereotyping:</strong> Assumptions based on appearance are often wrong</li>
        <li><strong>Rushing:</strong> There's no time limit on regular questions – use it</li>
        <li><strong>Overthinking:</strong> Sometimes the straightforward answer is correct</li>
        <li><strong>Tilt:</strong> Don't let a wrong answer affect your next guess</li>
      </ul>

      <h2>Final Thoughts</h2>
      <p>The best JailBirds players combine intuition with analytical thinking. They've learned to balance quick judgments with careful consideration, and they don't let emotions affect their gameplay.</p>

      <p>Most importantly, they remember that it's a game. Have fun with it, learn from each round, and the high scores will follow!</p>
    `
  },
  'bonus-round-guide': {
    title: "Understanding the JailBird Bonus Round",
    date: "December 18, 2025",
    category: "Game Guide",
    readTime: "4 min read",
    content: `
      <p class="lead">The JailBird Bonus round can make or break your final score. This guide explains exactly how it works and how to maximize your bonus points.</p>

      <h2>What is the Bonus Round?</h2>
      <p>After completing 11 regular questions, every JailBirds game ends with a special 12th question: the JailBird Bonus. This round works differently from the rest of the game, introducing a time pressure element that can significantly boost your score.</p>

      <h2>How Scoring Works</h2>
      <p>The bonus round uses a time-based multiplier system. The faster you answer correctly, the more points you earn:</p>

      <table class="scoring-table">
        <thead>
          <tr>
            <th>Response Time</th>
            <th>Bonus Points</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>0-3 seconds</td>
            <td>250-300 points</td>
          </tr>
          <tr>
            <td>3-5 seconds</td>
            <td>200-250 points</td>
          </tr>
          <tr>
            <td>5-8 seconds</td>
            <td>150-200 points</td>
          </tr>
          <tr>
            <td>8-10 seconds</td>
            <td>100-150 points</td>
          </tr>
          <tr>
            <td>10+ seconds</td>
            <td>100 points (base)</td>
          </tr>
          <tr>
            <td>Wrong answer</td>
            <td>0 points</td>
          </tr>
        </tbody>
      </table>

      <h2>The Timer</h2>
      <p>Unlike regular questions, the bonus round displays a visible countdown timer. This creates pressure but also gives you useful information – you can see exactly how much time you have and pace yourself accordingly.</p>

      <h2>Strategies for Success</h2>

      <h3>1. Prepare Mentally</h3>
      <p>Before the bonus round begins, take a moment to prepare. Clear your mind, take a breath, and get ready to focus. The transition from the 11th question to the bonus round gives you this opportunity.</p>

      <h3>2. Quick Scan Method</h3>
      <p>As soon as the question appears:</p>
      <ol>
        <li>Glance at the mugshot (1 second)</li>
        <li>Read all four options quickly (2-3 seconds)</li>
        <li>Make your selection (1-2 seconds)</li>
      </ol>
      <p>This method typically results in a 4-6 second answer time, earning solid bonus points while maintaining accuracy.</p>

      <h3>3. Elimination Strategy</h3>
      <p>If an answer isn't immediately obvious, quickly eliminate choices that seem clearly wrong. Getting down to 2-3 options makes the final decision easier and faster.</p>

      <h3>4. Trust Your Instincts</h3>
      <p>Research shows that first instincts are often correct. In time-pressure situations, going with your gut feeling tends to perform better than deliberate analysis.</p>

      <h2>Common Mistakes</h2>

      <h3>Panicking</h3>
      <p>The timer can cause anxiety, leading to rushed, poorly-considered answers. Remember: a correct answer in 8 seconds is better than a wrong answer in 3 seconds.</p>

      <h3>Over-analyzing</h3>
      <p>Taking too long trying to be certain can cost you valuable bonus points. There's a balance between speed and accuracy.</p>

      <h3>Ignoring the Timer</h3>
      <p>Some players get so focused on the question that they forget about the timer entirely. Keep it in your peripheral awareness.</p>

      <h2>Practice Makes Perfect</h2>
      <p>The more games you play, the better you'll get at the bonus round. You'll develop a natural rhythm and learn to trust your quick assessments.</p>

      <h2>The Math of the Bonus Round</h2>
      <p>A perfect game with a fast bonus answer can score over 1,400 points:</p>
      <ul>
        <li>11 correct regular answers: 1,100 points</li>
        <li>Fast bonus round correct: 300+ points</li>
        <li>Total: 1,400+ points</li>
      </ul>

      <p>This is why the leaderboard's top scores often depend on bonus round performance. Master this round, and you'll climb the rankings!</p>
    `
  },
  'ethics-of-mugshot-games': {
    title: "The Ethics of Mugshot Games: Our Approach",
    date: "December 18, 2025",
    category: "Editorial",
    readTime: "7 min read",
    content: `
      <p class="lead">Using real mugshots for entertainment raises important ethical questions. We believe in addressing these concerns openly and explaining the principles that guide our approach.</p>

      <h2>The Fundamental Question</h2>
      <p>Is it ethical to use real mugshots and arrest records for a game? This is a question we've thought deeply about, and we believe the answer depends on how it's done.</p>

      <h2>Public Records and Transparency</h2>
      <p>All the information in JailBirds comes from public records. In North Carolina, as in most U.S. states, arrest records are public information. The Mecklenburg County Sheriff's Office publishes this data online for anyone to access.</p>

      <p>We didn't create this system of public mugshots – it exists as part of the broader criminal justice system's commitment to transparency. By using this public data, we're not exposing anything that isn't already accessible.</p>

      <h2>Presumption of Innocence</h2>
      <p>This is perhaps our most important ethical principle: <strong>an arrest does not equal guilt</strong>. We make this clear throughout our game and in our legal disclaimers.</p>

      <p>Every person shown in JailBirds:</p>
      <ul>
        <li>Is presumed innocent until proven guilty</li>
        <li>May have had charges dropped or reduced</li>
        <li>May have been acquitted at trial</li>
        <li>May have had their record expunged</li>
      </ul>

      <p>We encourage players to remember this as they play. The game is about matching images to charges, not about judging individuals.</p>

      <h2>What We Don't Do</h2>
      <p>There are lines we won't cross:</p>

      <h3>No Personal Information</h3>
      <p>While arrest records often include addresses and other personal details, we only display the minimum information needed for the game: the mugshot, name, and charges.</p>

      <h3>No Minors</h3>
      <p>We never include juvenile records in our game, even in jurisdictions where such records might be partially public.</p>

      <h3>No Mockery</h3>
      <p>Our game presents mugshots straightforwardly, without adding humiliating captions, rankings of "worst" mugshots, or other content designed to mock individuals.</p>

      <h2>Educational Value</h2>
      <p>We believe JailBirds has genuine educational value. Playing the game can:</p>

      <ul>
        <li><strong>Challenge stereotypes:</strong> Players quickly learn that you can't judge a book by its cover</li>
        <li><strong>Raise awareness:</strong> The game exposes players to the reality of the criminal justice system</li>
        <li><strong>Spark curiosity:</strong> Many players become interested in learning more about criminal law and justice</li>
      </ul>

      <h2>The Bigger Picture</h2>
      <p>The existence of public mugshot databases is itself controversial. Critics argue that these databases:</p>
      <ul>
        <li>Can harm employment prospects for arrestees (even if never convicted)</li>
        <li>Disproportionately affect marginalized communities</li>
        <li>Create permanent digital records of temporary situations</li>
      </ul>

      <p>These are legitimate concerns about the criminal justice system itself. JailBirds doesn't create or perpetuate these issues – we simply present data that's already public.</p>

      <h2>Removal Requests</h2>
      <p>We take removal requests seriously. If someone contacts us because their mugshot appears in our game and they've been exonerated or had their record expunged, we'll work with them to address the situation appropriately.</p>

      <h2>Our Commitment</h2>
      <p>We're committed to:</p>
      <ol>
        <li>Always emphasizing the presumption of innocence</li>
        <li>Using only public information</li>
        <li>Treating subjects with basic dignity</li>
        <li>Being transparent about our data sources</li>
        <li>Responding to legitimate concerns</li>
      </ol>

      <h2>Final Thoughts</h2>
      <p>We've designed JailBirds to be entertaining while being mindful of the real people whose images appear in the game. We believe it's possible to create an engaging experience without being exploitative.</p>

      <p>If you have thoughts or concerns about our approach, we welcome feedback at <a href="mailto:ethics@jailbirdsgame.com">ethics@jailbirdsgame.com</a>.</p>
    `
  },
  'daily-streaks-and-achievements': {
    title: "New: Daily Streaks & Achievements Have Arrived",
    date: "June 2, 2026",
    category: "New Feature",
    readTime: "4 min read",
    content: `
      <p class="lead">JailBirds just got a whole lot more rewarding. We've launched Daily Streaks and a full Achievements system designed to make every game — and every day — count.</p>

      <h2>Keep Your Streak Alive</h2>
      <p>Play at least one game each day and your streak grows. Miss a day and it resets, so consistency is everything. Your current and longest streaks now show on your profile and pop up right on the Game Complete screen with a fiery banner. It's a simple, satisfying reason to come back tomorrow.</p>

      <h2>13 Achievements to Collect</h2>
      <p>From <strong>Rookie Detective</strong> (your very first game) to <strong>Unstoppable</strong> (a 30-day streak), there are 13 badges to unlock across bronze, silver, and gold tiers. Highlights include:</p>
      <ul>
        <li><strong>Eagle Eye</strong> — nail all 15 in a single game</li>
        <li><strong>Sharpshooter</strong> — score 1,000+ in one game</li>
        <li><strong>JailBird Legend</strong> — win 10 bonus rounds</li>
        <li><strong>Point Master</strong> — reach 25,000 total points</li>
      </ul>

      <h2>Your Past Counts, Too</h2>
      <p>Here's the best part: we didn't start everyone from zero. The moment you open your profile, JailBirds scans your entire game history and instantly awards every achievement you've already earned — including streaks reconstructed from your past play. Returning players get a one-time celebration toast showing exactly what they unlocked.</p>

      <h2>Get Notified the Moment You Win</h2>
      <p>Complete a new achievement mid-game and you'll get an instant "Achievement Unlocked!" alert, with the badge waiting for you on the results screen. Open your profile any time to see your full collection and how close you are to the next one.</p>

      <p>Jump in, start a streak today, and see how many badges you can collect!</p>
    `
  },
  'emoji-reactions-launch': {
    title: "React to Every Mugshot: Emoji Reactions Are Live",
    date: "June 5, 2026",
    category: "New Feature",
    readTime: "3 min read",
    content: `
      <p class="lead">Some mugshots just make you feel something. Now you can show it. Emoji Reactions are live on every arrest record in the game.</p>

      <h2>Six Ways to React</h2>
      <p>Under each mugshot you'll find a compact reaction bar with six emojis: 👍 thumbs up, 👎 thumbs down, ❤️ heart, 🔥 fire, 🍆 eggplant, and 💦 splash. Tap one to react, tap again to undo. You can stack multiple reactions on the same photo — go wild.</p>

      <h2>See What the Crowd Thinks</h2>
      <p>Every reaction is counted globally, so the numbers next to each emoji show how the entire JailBirds community feels about that face. It's a fun, instant pulse-check that turns solo play into a shared experience.</p>

      <h2>Yes, Even the Bonus Round</h2>
      <p>We made sure reactions work everywhere — including the high-stakes JailBird Bonus round. So when that final mugshot drops, you can react before (or after) you lock in your guess.</p>

      <p>Reactions also power some exciting new features you'll read about soon — like our Most Reacted gallery. Start tapping and let the community know what you think!</p>
    `
  },
  'most-reacted-mugshots-gallery': {
    title: "Introducing the 10 Most Reacted Mugshots",
    date: "June 8, 2026",
    category: "New Feature",
    readTime: "3 min read",
    content: `
      <p class="lead">You've been reacting in droves — so we built a place to celebrate the booking photos that get the biggest response.</p>

      <h2>The Community's Picks, Front and Center</h2>
      <p>The new <strong>10 Most Reacted Mugshots</strong> gallery automatically ranks the records that have collected the most emoji reactions. Each compact card shows the mugshot, a flame badge with the total reaction count, and a quick breakdown of the top emojis it earned.</p>

      <h2>Everywhere You Look</h2>
      <p>We've placed the gallery where you'll actually see it:</p>
      <ul>
        <li>On the <strong>home page</strong>, above the Hall of Champions</li>
        <li>On the <strong>leaderboard</strong>, above Top Players</li>
        <li>On the <strong>Community Stats</strong> page, above the Hall of Fame</li>
      </ul>

      <h2>Always Fresh</h2>
      <p>Because it's driven by live reaction data, the gallery is constantly shifting. React to a mugshot you love and you might just push it into the top 10. It's a living leaderboard for the faces of JailBirds.</p>

      <p>And clicking a card does something special — but we'll save that for the next post. 😉</p>
    `
  },
  'hall-of-shame-shareable-cards': {
    title: "Share the Laughs with Hall of Shame Cards",
    date: "June 10, 2026",
    category: "Community",
    readTime: "3 min read",
    content: `
      <p class="lead">Clicking a card in the Most Reacted gallery now unlocks a slick, ready-to-share "Hall of Shame" card — built for spreading the fun.</p>

      <h2>A Poster-Worthy Moment</h2>
      <p>Each Hall of Shame card wraps the mugshot in a bold poster design complete with the reaction totals, the standout emojis, and JailBirds branding. It's the kind of thing you'll want to post the second you see it.</p>

      <h2>One-Tap Sharing</h2>
      <p>From the card you can:</p>
      <ul>
        <li><strong>Download the image</strong> as a high-quality PNG to post anywhere</li>
        <li><strong>Share to Facebook</strong> — perfect for our community group</li>
        <li><strong>Share to X</strong> with a pre-written caption</li>
        <li><strong>Copy the caption</strong> to paste wherever you like</li>
      </ul>

      <h2>Built for the Group</h2>
      <p>Our Facebook community thrives on the funniest finds, and these cards make it effortless to contribute. Spot a legendary mugshot? Turn it into a card and drop it in the group for instant reactions.</p>

      <p>Head to the leaderboard, tap a most-reacted face, and make your first card today.</p>
    `
  },
  'community-stats-deep-dive': {
    title: "Dive Into the New Community Stats Page",
    date: "June 12, 2026",
    category: "New Feature",
    readTime: "4 min read",
    content: `
      <p class="lead">Ever wondered how the whole JailBirds community is performing — and where you fit in? The Community Stats page has the answers.</p>

      <h2>Charts That Tell the Story</h2>
      <p>We've packed the page with interactive visualizations powered by live data: total games played, score distributions, and trends that reveal how players across the world are doing. It's a fascinating snapshot of the entire community at a glance.</p>

      <h2>The Hall of Fame</h2>
      <p>Our Hall of Fame celebrates the all-time legends — the sharpest detectives and the most dedicated players who've left their mark on the leaderboard.</p>

      <h2>A Global Leaderboard You Can Actually Use</h2>
      <p>Scroll down and you'll find the Global Leaderboard Rankings: a complete, searchable list of every player. Features include:</p>
      <ul>
        <li><strong>Search and filter</strong> to find any player by name</li>
        <li><strong>Pagination</strong> so the list stays fast as the community grows</li>
        <li><strong>"Jump to my rank"</strong> — one click takes you straight to your spot</li>
      </ul>

      <h2>Find Your Place</h2>
      <p>Whether you're chasing the top 10 or just curious how you compare, Community Stats gives you the full picture. Check it out and see how far you've climbed.</p>
    `
  },
  'gold-tier-celebrations': {
    title: "Gold-Tier Wins Now Get a Confetti Celebration",
    date: "June 15, 2026",
    category: "New Feature",
    readTime: "2 min read",
    content: `
      <p class="lead">The rarest achievements deserve a real celebration — so now they get one.</p>

      <h2>Confetti for the Big Moments</h2>
      <p>Unlock a gold-tier achievement — like Eagle Eye, Point Master, or Unstoppable — and JailBirds erupts in a burst of gold confetti right as the Game Complete screen reveals your new badge. It's the perfect victory-lap moment.</p>

      <h2>Tasteful, Not Over-the-Top</h2>
      <p>We kept it classy: a quick, elegant burst that lands exactly when your achievement appears, then gracefully fades. It only fires for gold-tier unlocks, so it stays special — and it respects reduced-motion accessibility settings.</p>

      <h2>Made for Screenshots</h2>
      <p>That golden moment is begging to be captured and shared. Snap a screenshot of your confetti-soaked achievement and post it to the Facebook group to flex on your fellow detectives.</p>

      <p>Now go earn one!</p>
    `
  },
  'daily-pipeline-upgrade': {
    title: "Fresher Than Ever: Inside Our Daily Mugshot Pipeline",
    date: "June 18, 2026",
    category: "Behind the Scenes",
    readTime: "4 min read",
    content: `
      <p class="lead">Fresh faces every day are the heartbeat of JailBirds. Recently we gave the system that delivers them a serious tune-up.</p>

      <h2>The Challenge of Daily Data</h2>
      <p>Every day, our automated scraper pulls the latest arrest records from public Mecklenburg County data, validates them, and prepares a fresh batch for gameplay. Doing this reliably — day in, day out — is harder than it sounds.</p>

      <h2>Squashing the Empty-Day Gremlins</h2>
      <p>Occasionally, temporary rate limits or environment hiccups could cause a scrape to come back empty. We hardened the pipeline to be far more resilient, so the system self-recovers and keeps the daily drop of fresh, playable records flowing.</p>

      <h2>Quality First</h2>
      <p>Not every record makes the cut. We still filter for clear mugshots and real charges, ensuring each day's batch is high quality. On a typical day that means dozens of fresh, complete records ready for you to test your instincts against.</p>

      <h2>What's Next</h2>
      <p>We're continuing to invest in capturing more detailed charges per batch and expanding our coverage over time. The goal is simple: more fresh faces, more variety, and a game that never feels stale.</p>

      <p>Play today and you'll be guessing on records that didn't even exist 24 hours ago.</p>
    `
  },
  'climbing-the-global-leaderboard': {
    title: "How to Climb the Global Leaderboard in 2026",
    date: "June 20, 2026",
    category: "Tips & Tricks",
    readTime: "5 min read",
    content: `
      <p class="lead">With streaks, achievements, and bonus rounds all in the mix, climbing the ranks has more layers than ever. Here's your updated game plan.</p>

      <h2>1. Show Up Every Day</h2>
      <p>Streaks reward consistency, and consistency builds points. Even one game a day keeps your streak alive, chips away at cumulative achievements, and steadily grows your total score. The regulars at the top didn't get there in one session — they got there by showing up.</p>

      <h2>2. Master the Bonus Round</h2>
      <p>The JailBird Bonus is still where games are won or lost. A fast, correct bonus answer can add 250–300 points. Practice the quick-scan method: glance at the mugshot, read all options, and commit. Speed plus accuracy is the cheat code.</p>

      <h2>3. Chase Achievements Strategically</h2>
      <p>Many achievements — like Point Collector and Veteran Investigator — come naturally as you play. Others, like Eagle Eye (a perfect game), are worth deliberately hunting. Each unlock is a milestone that marks real progress.</p>

      <h2>4. Don't Trust Stereotypes</h2>
      <p>Our veterans say it again and again: the obvious-looking answer is often wrong. The game loves cases that defy expectations. Read every option and resist the gut-level snap judgment.</p>

      <h2>5. Use the Tools</h2>
      <p>Hit "Jump to my rank" on the Community Stats page to see exactly who's just ahead of you. Nothing motivates a climb like a target one spot above.</p>

      <p>Stack these habits and watch your name rise. See you at the top!</p>
    `
  },
  'join-the-facebook-community': {
    title: "Join the JailBirds Facebook Community",
    date: "June 22, 2026",
    category: "Community",
    readTime: "2 min read",
    content: `
      <p class="lead">JailBirds is more fun with friends — and the party is happening in our Facebook group.</p>

      <h2>Where the Fun Lives</h2>
      <p>Our community group is where players share their funniest mugshot finds, brag about high scores, swap strategies, and talk a little friendly trash. It's the social heart of the JailBirds experience.</p>

      <h2>One Tap to Join</h2>
      <p>We've added a sleek Facebook Group button right in the app, tucked under the "View Leaderboard" button. One tap and you're in — no hunting required.</p>

      <h2>Bring Your Best Cards</h2>
      <p>Remember those shareable Hall of Shame cards? The group is the perfect place to post them. Drop your most-reacted finds and watch the comments roll in.</p>

      <p>Come say hi — we can't wait to see what you share.</p>
    `
  },
  'advertise-with-jailbirds': {
    title: "Advertise With JailBirds: Reach Thousands of Engaged Players",
    date: "June 24, 2026",
    category: "Advertising",
    readTime: "5 min read",
    content: `
      <p class="lead">JailBirds isn't just a game — it's a fast-growing, highly engaged audience of players who show up every single day. If you're looking to put your brand in front of attentive eyeballs, we've built an advertising program just for you.</p>

      <h2>Why Advertise on JailBirds?</h2>
      <p>Our players aren't passive scrollers. They're actively focused, making decisions, reacting, and competing — game after game. That kind of attention is exactly what advertisers dream about. And with daily streaks, achievements, and fresh content pulling players back, our audience keeps growing and keeps coming back.</p>

      <h2>In-Game Video Ad Placements</h2>
      <p>The centerpiece of our program is premium in-game video advertising. Here's how it works:</p>
      <ul>
        <li><strong>Full-screen interstitial video ads</strong> play between rounds, where attention is naturally highest</li>
        <li><strong>Exactly three placements per session</strong> — frequent enough to deliver impressions, spaced enough to keep players happy</li>
        <li><strong>Your own MP4 creative</strong> — bring your existing video ads and we'll serve them</li>
      </ul>

      <h2>An Audience That Shares</h2>
      <p>JailBirds players are wired to share — from Hall of Shame cards to score brags in our active Facebook community. That built-in virality means your brand can travel beyond the game itself, earning organic reach you can't buy directly.</p>

      <h2>Perfect For</h2>
      <ul>
        <li>Local businesses targeting the Charlotte / Mecklenburg County area</li>
        <li>Brands seeking high-attention, full-screen video impressions</li>
        <li>Apps and games looking for engaged, entertainment-loving users</li>
        <li>Anyone who wants a memorable, uncluttered ad environment</li>
      </ul>

      <h2>Get Started Today</h2>
      <p>Ready to reach our audience? Visit our dedicated <a href="/ads">advertising page</a> to learn more about placements, formats, and availability — or reach out directly and we'll help you launch your first campaign.</p>

      <p>JailBirds is growing fast. Get your brand in front of our players while the spots are still wide open.</p>
    `
  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const staticPost = blogPosts[slug];
  const [post, setPost] = useState(staticPost || null);
  const [loading, setLoading] = useState(!staticPost);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (staticPost) {
      setPost(staticPost); setLoading(false); setNotFound(false);
      return;
    }
    setLoading(true); setNotFound(false);
    fetch(`${API}/api/blog/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setPost(d.post); setLoading(false); })
      .catch(() => { setLoading(false); setNotFound(true); });
  }, [slug, staticPost]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" data-testid="blog-post-loading" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-heading font-bold mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-6">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isDynamic = !staticPost;
  const heroImage = isDynamic ? proxied(post.image) : blogImages[slug];
  const gallery = Array.isArray(post.gallery) ? post.gallery : [];
  const shareUrl = `https://jailbirdsgame.com/blog/${slug}`;
  const shareText = post.title;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back Link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all posts
          </Link>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-medium px-3 py-1 rounded-full bg-primary/20 text-primary">
                {post.category}
              </span>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              {post.readTime && (
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6 leading-tight">
              {post.title}
            </h1>
            {heroImage && (
              <div className="aspect-[16/9] w-full overflow-hidden rounded-xl border border-border bg-muted mb-2">
                <img
                  src={heroImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </header>

          {/* Article Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-heading prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
              prose-ul:text-muted-foreground prose-ol:text-muted-foreground
              prose-li:mb-2
              prose-strong:text-foreground
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              [&_.lead]:text-xl [&_.lead]:text-foreground [&_.lead]:leading-relaxed [&_.lead]:mb-6
              [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
              [&_th]:text-left [&_th]:py-3 [&_th]:px-4 [&_th]:border-b [&_th]:border-border [&_th]:font-semibold
              [&_td]:py-3 [&_td]:px-4 [&_td]:border-b [&_td]:border-border/50 [&_td]:text-muted-foreground
            "
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Mugshot gallery (dynamic scrape posts) */}
          {gallery.length > 0 && (
            <div className="mt-10" data-testid="blog-gallery">
              <h2 className="text-2xl font-heading font-bold mb-4">The New Lineup</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {gallery.map((g, i) => (
                  <div key={i} className="aspect-square overflow-hidden rounded-lg border border-border bg-muted">
                    <img
                      src={proxied(g.image)}
                      alt={g.name || 'Mugshot'}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Think you can guess their charges? <Link to="/game" className="text-primary hover:underline font-medium">Play the game →</Link>
              </p>
            </div>
          )}

          {/* Share Section */}
          <div className="border-t border-border mt-12 pt-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Share2 className="w-5 h-5" />
                <span>Share this article:</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Related Posts / CTA */}
          <div className="bg-card border border-border rounded-lg p-8 mt-8 text-center">
            <BirdMascot size={48} className="mx-auto mb-4" />
            <h2 className="text-xl font-heading font-bold mb-2">Ready to Play?</h2>
            <p className="text-muted-foreground mb-4">
              Test your detective instincts with real mugshots!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button className="gap-2">
                  Play Now
                </Button>
              </Link>
              <Link to="/blog">
                <Button variant="outline">
                  More Articles
                </Button>
              </Link>
            </div>
          </div>

        </motion.article>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
